import { SCROLL_AXES } from "./scroll-box-axes"
import { detectCutOffContent } from "./scroll-box-cut-off-content"
import { detectCutPopups } from "./scroll-box-cut-popup"
import { isClippedOutOfSight } from "./scroll-box-effective-size"
import type { DetectedFinding, ScrollBoxFinding, TooSmallScrollBoxFinding } from "./scroll-box-findings"
import { detectHiddenEnds, detectPageHiddenEnds } from "./scroll-box-hidden-end"
import { identifyScrollBox } from "./scroll-box-identity"
import { detectContentOutsideWindowInLayer, detectPageContentOutsideWindow } from "./scroll-box-outside-window"
import { clipsOrScrollsAlong, hidesOwnContentAlong, isFixedPositioned, isPageRoot, isRendered } from "./scroll-box-styles"
import { detectUnusedRoom } from "./scroll-box-unused-room"
import { judgeScrollBox, type ScrollBoxVerdict } from "./scroll-box-verdict"

export function isOverflowingScrollBox(element: Element): boolean {
  return !isPageRoot(element) && isRendered(element) && SCROLL_AXES.some((axis) => hidesOwnContentAlong(element, axis))
}

export function findOverflowingScrollBoxes(root: ParentNode): Element[] {
  return Array.from(root.querySelectorAll("*")).filter(isOverflowingScrollBox)
}

export function isInspectedElement(element: Element): boolean {
  return (
    !isPageRoot(element) && (SCROLL_AXES.some((axis) => clipsOrScrollsAlong(element, axis)) || isFixedPositioned(element))
  )
}

function detectTooSmallScrollBox(element: Element, verdict: ScrollBoxVerdict): TooSmallScrollBoxFinding[] {
  if (!verdict.isTooSmall) return []
  return [
    {
      kind: "too-small",
      identity: identifyScrollBox(element),
      boxWidthPx: element.clientWidth,
      boxHeightPx: element.clientHeight,
      verdict,
    },
  ]
}

function detectScrollBoxFindings(element: Element, minimumWholeUnits: number): ScrollBoxFinding[] {
  if (!isOverflowingScrollBox(element)) return []
  const verdict = judgeScrollBox(element, minimumWholeUnits)
  return [...detectTooSmallScrollBox(element, verdict), ...detectUnusedRoom(element, verdict)]
}

export function detectElementFindings(element: Element, minimumWholeUnits: number): ScrollBoxFinding[] {
  if (isClippedOutOfSight(element)) return []
  return [
    ...detectScrollBoxFindings(element, minimumWholeUnits),
    ...detectCutOffContent(element),
    ...detectContentOutsideWindowInLayer(element),
    ...detectHiddenEnds(element),
    ...detectCutPopups(element),
  ]
}

export function detectPageFindings(): DetectedFinding[] {
  return [...detectPageContentOutsideWindow(), ...detectPageHiddenEnds()]
}

export function sweepScrollBoxFindings(root: Element | Document, minimumWholeUnits: number): ScrollBoxFinding[] {
  const elementFindings = Array.from(root.querySelectorAll("*"))
    .filter(isInspectedElement)
    .flatMap((element) => detectElementFindings(element, minimumWholeUnits))
  const pageFindings = detectPageFindings()
    .filter((detected) => root.contains(detected.element))
    .map((detected) => detected.finding)
  return [...elementFindings, ...pageFindings]
}
